/**
 * PAY — one screen, four views, one subtraction.
 *
 * Anthony listed payroll, payouts, hours and my pay as four things,
 * because that is how the old system had them. They are four views of
 * earned minus paid, and keeping them apart is what makes somebody read
 * hours on one screen, payments on another, and do the sum in their head.
 *
 * WHO SEES WHAT. A technician gets My pay and nothing else — their own
 * hours, their own money, and no sight of anybody else's rate. Admins and
 * supervisors get all four. This is an interface rule and it is honest
 * about that: row level security stops another SHOP reading these rows,
 * but a determined technician with a console could read a colleague's
 * hours. Closing that properly needs per-row policies keyed on the
 * technician's own profile, which is worth doing before this carries a
 * shop where that matters.
 */

import {
  payrun, hoursIn, payoutsIn, technicians, logHours, recordPayout, meAsTech, setCovers,
  correctHours, removeHours, statement, addBonus, removeBonus, recordKeptCash, setPartCost,
  savePlan, termsFor, onTheClock, punchesIn,
  weekStart, addDays, type Line, type Tech, type Hours, type Terms, type Statement,
} from "../data/pay.ts";
import { buildCard, cardHtml, saveCard, planLine, roleName, type Card } from "./pay-statement.ts";
import { esc, money, mount } from "../ui/dom.ts";
import { shopDayKey } from "../ui/shopclock.ts";
import { splitOvertime } from "../domain/overtime.ts";
import { buildExport, punchesForWeek, toCsv, toJson } from "../domain/payroll-export.ts";
import type { Role } from "../domain/permissions.ts";
import { drawClockPanel, fixClockedHours } from "./clock.ts";

type Tab = "payroll" | "hours" | "payouts" | "plans" | "mine";

const TABS: Array<{ key: Tab; label: string; admin: boolean }> = [
  { key: "payroll", label: "Pay run", admin: true },
  { key: "plans", label: "Pay plans", admin: true },
  { key: "hours", label: "Hours", admin: true },
  { key: "payouts", label: "Payouts", admin: true },
  { key: "mine", label: "My pay", admin: false },
];

const BOSS: Role[] = ["admin", "supervisor"];

/** The person whose statement is open on the pay run, if any. */
let person: string | null = null;

let tab: Tab = "payroll";
let from = weekStart();
let to = addDays(weekStart(), 6);

const dt = (iso: string) =>
  new Date(`${iso}T12:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" });

/** Clock times are Pacific, the same zone 0191 dates the shift in. */
const PACIFIC = "America/Los_Angeles";
const clockTime = (iso: string) => new Date(iso).toLocaleString("en-US", {
  timeZone: PACIFIC, weekday: "short", hour: "numeric", minute: "2-digit",
});
const sinceText = (iso: string) => {
  const mins = Math.max(0, Math.floor((Date.now() - Date.parse(iso)) / 60000));
  return `${Math.floor(mins / 60)}h ${String(mins % 60).padStart(2, "0")}m`;
};

function periodBar(): string {
  return `
    <div class="listbar">
      <div class="period">
        <button class="btn ghost sm" id="p-prev">&lsaquo; Previous</button>
        <span class="period-now">${esc(dt(from))} &ndash; ${esc(dt(to))}</span>
        <button class="btn ghost sm" id="p-next">Next &rsaquo;</button>
      </div>
      <button class="btn ghost sm" id="p-this">This week</button>
    </div>`;
}

function wirePeriod(redraw: () => void): void {
  const shift = (days: number) => { from = addDays(from, days); to = addDays(to, days); redraw(); };
  document.getElementById("p-prev")?.addEventListener("click", () => shift(-7));
  document.getElementById("p-next")?.addEventListener("click", () => shift(7));
  document.getElementById("p-this")?.addEventListener("click", () => {
    from = weekStart(); to = addDays(from, 6); redraw();
  });
}

export async function payPage(role: Role, want?: string): Promise<void> {
  const boss = BOSS.includes(role);
  // A technician has exactly one view, so do not offer them three empty ones.
  if (want && TABS.some((t) => t.key === want)) tab = want as Tab;
  if (!boss) tab = "mine";

  const shown = TABS.filter((t) => boss || !t.admin);

  mount(`<div class="page">
    <header class="page-head"><a class="back" href="#/more">&larr; Everything else</a>
      <h1>Pay</h1>
      <p>${boss
        ? "Earned, minus what has actually been handed over. A payout row is the only proof somebody was paid."
        : "Your hours and your money."}</p></header>
    <div class="tabs wide" id="paytabs">
      ${shown.map((t) => `<button class="tab ${t.key === tab ? "on" : ""}"
        data-t="${t.key}">${esc(t.label)}</button>`).join("")}
    </div>
    ${periodBar()}
    <div id="paybody"><div class="loading">Counting&hellip;</div></div>
  </div>`);

  document.querySelectorAll<HTMLButtonElement>("#paytabs .tab").forEach((b) =>
    b.addEventListener("click", () => { tab = b.dataset["t"] as Tab; person = null; void payPage(role); }));
  wirePeriod(() => void payPage(role));

  const body = document.getElementById("paybody");
  if (!body) return;

  try {
    if (tab === "payroll" && person) await drawPerson(body, role, person);
    else if (tab === "payroll") await drawPayrun(body, role);
    else if (tab === "plans") await drawPlans(body, role);
    else if (tab === "hours") await drawHours(body, role);
    else if (tab === "payouts") await drawPayouts(body, role);
    else await drawMine(body);
  } catch (e) {
    body.innerHTML = `<div class="card"><div class="empty bad">${
      esc(e instanceof Error ? e.message : String(e))}</div></div>`;
  }
}

/* ── the pay run ──────────────────────────────────────────────────── */

/**
 * THE PAY RUN, laid out like the workforce platforms lay theirs out: one
 * row per person, who they are and what deal they are on, then earned,
 * paid and owed, and a status you can read from across the room. A tap
 * opens that person's statement card.
 */
async function drawPayrun(host: HTMLElement, role: Role): Promise<void> {
  const [lines, terms] = await Promise.all([payrun(from, to), termsFor()]);
  const owed = lines.reduce((t, l) => t + Math.max(0, l.owed), 0);
  const earned = lines.reduce((t, l) => t + l.earned, 0);
  const due = lines.filter((l) => l.owed > 0.005 && !terms.get(l.tech.id)?.settles_own);
  const done = lines.filter((l) => Math.abs(l.owed) <= 0.005 || terms.get(l.tech.id)?.settles_own);

  host.innerHTML = `
    <div class="prun-steps">
      <div class="prun-step ${due.length ? "on" : "done"}"><b>1</b> Review statements</div>
      <div class="prun-step ${due.length ? "" : "done"}"><b>2</b> Pay ${due.length} ${due.length === 1 ? "person" : "people"}</div>
      <div class="prun-step ${due.length ? "" : "done"}"><b>3</b> Square</div>
    </div>
    <div class="mtiles">
      <div class="mtile money"><div class="mtile-l">Earned</div>
        <div class="mtile-v">${money(earned)}</div>
        <div class="mtile-n">jobs, hours, bonuses and money back</div></div>
      <div class="mtile money"><div class="mtile-l">Still owed</div>
        <div class="mtile-v">${money(owed)}</div>
        <div class="mtile-n">${due.length} to pay, ${done.length} square</div></div>
    </div>
    <div class="card ptable">
      <div class="ptable-head"><span>Person</span><span>Earned</span><span>Paid</span><span>Owed</span></div>
      ${lines.length ? lines.map((l) => personRow(l, terms.get(l.tech.id))).join("")
        : `<div class="empty">Nobody worked in this window.</div>`}
    </div>`;

  host.querySelectorAll<HTMLButtonElement>("[data-person]").forEach((b) =>
    b.addEventListener("click", () => { person = b.dataset["person"] ?? null; void payPage(role); }));
}

const initials = (name: string) =>
  name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase()).join("");

function personRow(l: Line, t: Terms | undefined): string {
  const own = !!t?.settles_own;
  const state = own ? ["square", "Pays himself"]
    : l.owed > 0.005 ? ["owed", "To pay"]
    : l.owed < -0.005 ? ["ahead", "Ahead"] : ["square", "Square"];
  return `<button class="ptable-row" data-person="${esc(l.tech.id)}">
    <span class="ptable-who">
      <span class="pav">${esc(initials(l.tech.name))}</span>
      <span><b>${esc(l.tech.name)}</b>
        <small>${esc(roleName(t?.role))} · ${esc(planLine(t ?? null))}</small></span>
    </span>
    <span class="ptable-n" data-l="Earned">${money(l.earned)}</span>
    <span class="ptable-n" data-l="Paid">${money(l.paid)}</span>
    <span class="ptable-n" data-l="Owed"><b>${money(l.owed)}</b>
      <span class="pchip t-${state[0]}">${state[1]}</span></span>
  </button>`;
}

/* ── one person's statement ───────────────────────────────────────── */

let lastCard: Card | null = null;

async function drawPerson(host: HTMLElement, role: Role, techId: string): Promise<void> {
  const s = await statement(techId, from, to);
  const card = buildCard(s, from, to, "Supercanic");
  lastCard = card;
  const boss = BOSS.includes(role);

  host.innerHTML = `
    <button class="btn ghost sm" id="pc-back">&larr; Everyone</button>
    ${cardHtml(card, boss)}
    <div class="pstate-acts">
      <button class="btn primary" id="pc-save">Save as image</button>
      ${boss ? `
        <button class="btn ghost" id="pc-bonus">Add a bonus</button>
        <button class="btn ghost" id="pc-cash">Cash they kept</button>
        <button class="btn ghost" id="pc-pay">Record a payout</button>` : ""}
    </div>`;

  const redraw = () => void drawPerson(host, role, techId);
  document.getElementById("pc-back")?.addEventListener("click", () => { person = null; void payPage(role); });
  document.getElementById("pc-save")?.addEventListener("click", () => void saveImage(s));
  document.getElementById("pc-bonus")?.addEventListener("click", () => bonusForm(s, redraw));
  document.getElementById("pc-cash")?.addEventListener("click", () => cashForm(s, redraw));
  document.getElementById("pc-pay")?.addEventListener("click", () =>
    void payoutForm(role, [{ tech: s.tech, owed: s.owed } as Line], redraw));

  host.querySelectorAll<HTMLButtonElement>(".pstate-act").forEach((b) =>
    b.addEventListener("click", () => {
      const [kind, a, c] = (b.dataset["act"] ?? "").split(":");
      if (kind === "cost" && a && c) costForm(s, a, c, redraw);
      if (kind === "bonus" && a) void dropBonus(b, a, redraw);
    }));
}

async function saveImage(s: Statement): Promise<void> {
  if (!lastCard) return;
  const name = `${s.tech.name.replace(/[^A-Za-z0-9]+/g, "-")}-Pay-${to}.png`;
  try { await saveCard(lastCard, name); } catch (e) { alert(e instanceof Error ? e.message : String(e)); }
}

async function dropBonus(b: HTMLButtonElement, id: string, redraw: () => void): Promise<void> {
  if (b.dataset["sure"] !== "1") { b.dataset["sure"] = "1"; b.textContent = "really remove?"; return; }
  try { await removeBonus(id); redraw(); } catch (e) { alert(e instanceof Error ? e.message : String(e)); }
}

const today = () => shopDayKey(new Date());
const inWindow = () => (today() >= from && today() <= to ? today() : to);

function bonusForm(s: Statement, done: () => void): void {
  const dispatcher = s.terms?.role === "dispatcher";
  sheet(`Add to ${s.tech.name}'s pay`, `
    <label class="field"><span>What for</span>
      <select id="b-kind">
        <option value="bonus|Review" ${dispatcher ? "" : "selected"}>Review</option>
        <option value="bonus|Referral">Referral</option>
        <option value="dispatch|Dispatch call" ${dispatcher ? "selected" : ""}>Dispatch call</option>
        <option value="bonus|">Something else</option>
      </select></label>
    <label class="field"><span>Note</span><input id="b-note" placeholder="Which customer, which job"></label>
    <label class="field"><span>Amount</span>
      <input id="b-amt" type="number" step="0.01" value="${dispatcher ? "" : "25"}"></label>
    <label class="field"><span>Day</span><input id="b-day" type="date" value="${esc(inWindow())}"></label>`,
  "Add it");
  document.getElementById("ps-save")?.addEventListener("click", async () => {
    const btn = document.getElementById("ps-save") as HTMLButtonElement;
    const [basis, word] = val("b-kind").split("|");
    const why = [word, val("b-note")].filter(Boolean).join(", ");
    btn.disabled = true; btn.textContent = "Saving…";
    try {
      await addBonus(s.tech.id, Number(val("b-amt")), why, val("b-day"),
        basis === "dispatch" ? "dispatch" : "bonus");
      document.getElementById("paysheet")?.remove(); done();
    } catch (e) { btn.disabled = false; btn.textContent = "Add it"; fail(e instanceof Error ? e.message : String(e)); }
  });
}

function cashForm(s: Statement, done: () => void): void {
  sheet(`Cash ${s.tech.name.split(" ")[0]} kept`, `
    <p class="quiet small">A customer paid in cash and the tech kept it. It comes off what the
      shop owes them, the same as a payout.</p>
    <label class="field"><span>Amount</span><input id="c-amt" type="number" step="0.01"></label>
    <label class="field"><span>Which job or customer</span><input id="c-note"></label>
    <label class="field"><span>Day</span><input id="c-day" type="date" value="${esc(inWindow())}"></label>`,
  "Record it");
  document.getElementById("ps-save")?.addEventListener("click", async () => {
    const btn = document.getElementById("ps-save") as HTMLButtonElement;
    btn.disabled = true; btn.textContent = "Saving…";
    try {
      await recordKeptCash(s.tech.id, Number(val("c-amt")), val("c-day"), val("c-note"));
      document.getElementById("paysheet")?.remove(); done();
    } catch (e) { btn.disabled = false; btn.textContent = "Record it"; fail(e instanceof Error ? e.message : String(e)); }
  });
}

function costForm(s: Statement, jobId: string, lineId: string, done: () => void): void {
  const part = s.jobs.flatMap((j) => j.parts).find((p) => p.id === lineId);
  if (!part) return;
  sheet(`What ${part.description} really cost`, `
    <p class="quiet small">Sold to the customer for ${esc(money(part.sold))}. The markup is what the
      customer paid minus what the shop paid, and the tech's share is worked out again from it.</p>
    <label class="field"><span>What the shop paid</span>
      <input id="k-paid" type="number" step="0.01" value="${part.cost != null ? esc((part.cost * part.quantity).toFixed(2)) : ""}"></label>
    <label class="field"><span>Core refund that came back to the shop</span>
      <input id="k-core" type="number" step="0.01" value="0"></label>
    <p class="quiet small">If the tech kept the core refund, leave it at 0 here and record it as
      cash they kept.</p>`, "Save and recompute");
  document.getElementById("ps-save")?.addEventListener("click", async () => {
    const btn = document.getElementById("ps-save") as HTMLButtonElement;
    btn.disabled = true; btn.textContent = "Saving…";
    try {
      await setPartCost(lineId, Number(val("k-paid")), Number(val("k-core") || 0), jobId);
      document.getElementById("paysheet")?.remove(); done();
    } catch (e) { btn.disabled = false; btn.textContent = "Save and recompute"; fail(e instanceof Error ? e.message : String(e)); }
  });
}

/* ── pay plans ────────────────────────────────────────────────────── */

const ROLES: Array<[string, string]> = [
  ["mobile_tech", "Mobile tech"], ["shop_tech", "Shop tech"], ["franchise_tech", "Franchise tech"],
  ["advisor", "Service advisor"], ["apprentice", "Apprentice"], ["dispatcher", "Dispatcher"],
  ["owner", "Owner"],
];

/**
 * EVERY PERSON'S DEAL ON ONE SCREEN. Anthony went over all of them in
 * chat on 2026-10-03, and the answers lived in a migration. The next time
 * a rate changes it should change here, by the person who decides it.
 */
async function drawPlans(host: HTMLElement, role: Role): Promise<void> {
  const [techs, terms] = await Promise.all([technicians(), termsFor()]);
  const active = techs.filter((t) => t.active);
  host.innerHTML = `
    <div class="card ptable">
      <div class="ptable-head plans"><span>Person</span><span>Plan</span></div>
      ${active.map((t) => {
        const p = terms.get(t.id);
        return `<button class="ptable-row plans" data-plan="${esc(t.id)}">
          <span class="ptable-who"><span class="pav">${esc(initials(t.name))}</span>
            <span><b>${esc(t.name)}</b><small>${esc(roleName(p?.role))}</small></span></span>
          <span class="ptable-plan">${esc(planLine(p ?? null))}</span>
        </button>`;
      }).join("")}
    </div>
    <p class="quiet small">Shop jobs pay the shop rate. Mobile jobs, and jobs with no place
      recorded, pay the mobile rate. A change applies to work finished from now on. Work already on
      a statement keeps the rate it was earned at unless you fix a part cost on it.</p>`;

  host.querySelectorAll<HTMLButtonElement>("[data-plan]").forEach((b) =>
    b.addEventListener("click", () => {
      const t = active.find((x) => x.id === b.dataset["plan"]);
      if (t) planForm(t, terms.get(t.id), () => void drawPlans(host, role));
    }));
}

function planForm(t: Tech, p: Terms | undefined, done: () => void): void {
  const pct = (n: number | null | undefined) => (n == null ? "" : String(Math.round(Number(n) * 10000) / 100));
  sheet(`${t.name}'s pay plan`, `
    <label class="field"><span>Role</span><select id="pl-role">
      ${ROLES.map(([k, v]) => `<option value="${k}" ${p?.role === k ? "selected" : ""}>${v}</option>`).join("")}
    </select></label>
    <label class="field"><span>Paid by</span><select id="pl-basis">
      <option value="commission" ${p?.basis === "commission" ? "selected" : ""}>Commission</option>
      <option value="hourly" ${p?.basis === "hourly" ? "selected" : ""}>Hourly</option>
    </select></label>
    <label class="field"><span>Hourly rate</span>
      <input id="pl-rate" type="number" step="0.01" value="${p?.hourly_rate ?? ""}"></label>
    <div class="ed-sec">Mobile jobs, % of</div>
    <label class="field"><span>Labor</span><input id="pl-ml" type="number" step="0.001" value="${pct(p?.commission_pct)}"></label>
    <label class="field"><span>Parts markup</span><input id="pl-mp" type="number" step="0.001" value="${pct(p?.parts_pct)}"></label>
    <div class="ed-sec">Shop jobs, % of (blank uses the mobile rate)</div>
    <label class="field"><span>Labor</span><input id="pl-sl" type="number" step="0.001" value="${pct(p?.shop_commission_pct)}"></label>
    <label class="field"><span>Parts markup</span><input id="pl-sp" type="number" step="0.001" value="${pct(p?.shop_parts_pct)}"></label>
    <p class="quiet small">Type 66.667 for two thirds.</p>`, "Save the plan");

  document.getElementById("ps-save")?.addEventListener("click", async () => {
    const btn = document.getElementById("ps-save") as HTMLButtonElement;
    const frac = (id: string) => (val(id) === "" ? null : Math.round(Number(val(id)) * 1000) / 100000);
    const basis = val("pl-basis");
    btn.disabled = true; btn.textContent = "Saving…";
    try {
      await savePlan(t.id, {
        role: val("pl-role") || null, basis,
        hourly_rate: val("pl-rate") === "" ? null : Number(val("pl-rate")),
        // Hourly people earn no commission. Apprentices especially.
        commission_pct: basis === "hourly" ? null : frac("pl-ml"),
        parts_pct: basis === "hourly" ? null : frac("pl-mp"),
        shop_commission_pct: basis === "hourly" ? null : frac("pl-sl"),
        shop_parts_pct: basis === "hourly" ? null : frac("pl-sp"),
      });
      document.getElementById("paysheet")?.remove(); done();
    } catch (e) { btn.disabled = false; btn.textContent = "Save the plan"; fail(e instanceof Error ? e.message : String(e)); }
  });
}

/* ── hours ────────────────────────────────────────────────────────── */

async function drawHours(host: HTMLElement, role: Role): Promise<void> {
  const [rows, techs, onNow] = await Promise.all([hoursIn(from, to), technicians(), onTheClock()]);
  const nameOf = new Map(techs.map((t) => [t.id, t.name]));
  const total = rows.reduce((t, r) => t + Number(r.hours), 0);
  const splits = splitOvertime(rows);

  host.innerHTML = `
    ${onNow.length ? `<div class="card">
      <div class="card-head">On the clock now</div>
      ${onNow.map((c) => `<div class="row"><div class="row-main">
          <div class="row-title"><span class="num">${esc(nameOf.get(c.technician_id) ?? "Unknown")}</span></div>
          <div class="row-sub">since ${esc(clockTime(c.clock_in))} Pacific</div></div>
        <div class="row-side"><div class="amount">${esc(sinceText(c.clock_in))}</div></div></div>`).join("")}
    </div>` : ""}
    <div class="hoursacts">
      <button class="btn primary" id="log-hours">Log hours</button>
      <a class="btn ghost" href="#/timesheet">Timesheets</a>
    </div>
    <div class="card">
      <div class="card-head">${rows.length} ${rows.length === 1 ? "entry" : "entries"}
        &middot; ${total.toFixed(2)} hours</div>
      ${rows.length ? rows.map((r) => `
        <button class="row hoursrow" data-h="${esc(r.id)}">
          <div class="row-main">
            <div class="row-title"><span class="num">${esc(nameOf.get(r.technician_id) ?? "Unknown")}</span></div>
            <div class="row-sub">${esc(dt(r.worked_on))}${
              r.note ? `<span class="dot">&middot;</span>${esc(String(r.note).split("\n")[0] ?? "")}` : ""}</div>
          </div>
          <div class="row-side">
            <div class="amount">${Number(r.hours).toFixed(2)} h</div>
            <div class="when">${r.rate != null ? `at ${money(Number(r.rate))}` : "rate from terms"}${otText(splits.get(r.id))}</div>
          </div>
        </button>`).join("") : `<div class="empty">Nothing logged in this window.</div>`}
    </div>
    ${role === "admin" ? exportCard() : ""}`;

  document.getElementById("log-hours")?.addEventListener("click", () => void hoursForm(role, techs));
  if (role === "admin") wireExport(nameOf);

  // A shift opens for correcting. Payroll is the one place a wrong number
  // is somebody's wages, so it has to be fixable by the person who spots
  // it rather than by whoever can reach the database.
  //
  // A row the clock wrote is fixed at the punch instead (src/pages/clock.ts),
  // so its In, Out and meal breaks change with it and the export and the
  // pay run cannot disagree. A row typed in here keeps this correction.
  document.querySelectorAll<HTMLButtonElement>(".hoursrow").forEach((b) =>
    b.addEventListener("click", async () => {
      const row = rows.find((r) => r.id === b.dataset["h"]);
      if (!row) return;
      const who = nameOf.get(row.technician_id) ?? "Unknown";
      if (await fixClockedHours(role, row.id, who, () => void payPage(role)).catch(() => false)) return;
      fixHours(role, row, who);
    }));
}

/**
 * EXPORT FOR PAYROLL. Admins only, on the Hours tab, for the workweek the
 * arrows above are showing (Monday to Sunday, Pacific, the same week the
 * overtime split uses). Two downloads of the same punches: a CSV in the
 * columns the shop's California gross-pay calculator reads, and the raw
 * pay_clock rows with their meal breaks as JSON for the same tool.
 *
 * Nothing is computed here. The desk's pay run is hours times rate, and
 * W-2 pay in California is not, so the calculator and the payroll
 * provider do that part from these punches. Read-only: the same rows the
 * Hours list came from, through the same policy.
 */
function exportCard(): string {
  return `<div class="card pad" id="payroll-export">
    <div class="card-head">Export for payroll</div>
    <p class="quiet small">Clock punches for ${esc(dt(from))} &ndash; ${esc(dt(to))}, Pacific, one row per shift,
      dated by the day the shift started. Rest breaks are paid and not punched here, so that column is blank
      and the calculator flags them as unrecorded. A shift still on the clock is left out until it ends.</p>
    <div class="pstate-acts">
      <button class="btn ghost" id="pr-csv">Download CSV</button>
      <button class="btn ghost" id="pr-json">Download JSON</button>
    </div>
    <div class="msg-slot" id="pr-msg"></div>
  </div>`;
}

function wireExport(nameOf: Map<string, string>): void {
  const msg = (m: string, bad = false) => {
    const el = document.getElementById("pr-msg");
    if (el) el.innerHTML = `<p class="msg ${bad ? "bad" : "good"}">${esc(m)}</p>`;
  };
  const run = async (kind: "csv" | "json") => {
    const btn = document.getElementById(`pr-${kind}`) as HTMLButtonElement | null;
    if (btn) btn.disabled = true;
    try {
      const week = punchesForWeek(await punchesIn(from, to), from, to);
      const ex = buildExport(week, nameOf);
      if (!ex.rows.length) {
        msg(ex.stillOpen.length
          ? `No finished shifts this week yet. Still on the clock: ${ex.stillOpen.join(", ")}.`
          : "No clock punches in this week.", true);
        return;
      }
      const name = `shopdesk-punches-${from}.${kind}`;
      downloadText(name, kind === "csv" ? toCsv(ex.rows) : toJson(ex.punches, from, to),
        kind === "csv" ? "text/csv" : "application/json");
      msg(`${ex.rows.length} ${ex.rows.length === 1 ? "shift" : "shifts"} in ${name}.${
        ex.stillOpen.length ? ` Left out, still on the clock: ${ex.stillOpen.join(", ")}.` : ""}`);
    } catch (e) {
      msg(e instanceof Error ? e.message : String(e), true);
    } finally {
      if (btn) btn.disabled = false;
    }
  };
  document.getElementById("pr-csv")?.addEventListener("click", () => void run("csv"));
  document.getElementById("pr-json")?.addEventListener("click", () => void run("json"));
}

/** A text file handed to the browser as a download. */
function downloadText(fileName: string, text: string, mime: string): void {
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([text], { type: `${mime};charset=utf-8` }));
  a.download = fileName;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 5000);
}

/** "· 2.00 OT, 1.00 double" when a shift ran into overtime, else nothing. */
const otText = (sp: { ot: number; dt: number } | undefined): string =>
  sp && (sp.ot || sp.dt)
    ? ` &middot; ${sp.ot ? `${sp.ot.toFixed(2)} OT` : ""}${sp.ot && sp.dt ? ", " : ""}${sp.dt ? `${sp.dt.toFixed(2)} double` : ""}`
    : "";

/* ── payouts ──────────────────────────────────────────────────────── */

async function drawPayouts(host: HTMLElement, role: Role): Promise<void> {
  const boss = BOSS.includes(role);
  const [rows, techs] = await Promise.all([payoutsIn(from, to), technicians()]);
  const nameOf = new Map(techs.map((t) => [t.id, t.name]));
  const total = rows.reduce((t, r) => t + Number(r.amount), 0);

  host.innerHTML = `
    <button class="btn primary" id="pay-someone">Record a payout</button>
    <div class="card">
      <div class="card-head">${rows.length} paid &middot; ${money(total)}</div>
      ${rows.length ? rows.map((r) => `
        <div class="row">
          <div class="row-main">
            <div class="row-title"><span class="num">${esc(nameOf.get(r.technician_id) ?? "Unknown")}</span>
              <span class="pill">${esc(r.method)}</span></div>
            <!-- WHICH PERIOD THIS SETTLES, and a way to say so.
                 A payment with no period is filed on the day it was handed
                 over, which is right for ordinary pay and wrong for a
                 catch-up: three of those made Robert wilson read as $1,342
                 overpaid when he was owed $580. Rather than guess a date
                 to tidy the number, the person who knows can set it. -->
            <div class="row-sub">${r.covers_from && r.covers_to
              ? `covers ${esc(dt(r.covers_from))} &ndash; ${esc(dt(r.covers_to))}`
              : `<span class="noperiod">no period given</span>`}${
              r.reference ? `<span class="dot">&middot;</span>${esc(r.reference)}` : ""}</div>
          </div>
          <div class="row-side"><div class="amount">${money(Number(r.amount))}</div>
            ${boss ? `<button class="linkbtn" data-covers="${esc(r.id)}"
              data-from="${esc(r.covers_from ?? "")}" data-to="${esc(r.covers_to ?? "")}"
              >${r.covers_to ? "change period" : "set the period"}</button>` : ""}</div>
        </div>`).join("") : `<div class="empty">Nothing paid out in this window.</div>`}
    </div>`;

  document.getElementById("pay-someone")?.addEventListener("click", () => void payoutForm(role, []));

  host.querySelectorAll<HTMLButtonElement>("[data-covers]").forEach((b) =>
    b.addEventListener("click", async () => {
      const from = prompt(
        "What period does this payment cover?\n\nFirst day, as YYYY-MM-DD. "
        + "Leave blank to clear it.", b.dataset["from"] || "");
      if (from === null) return;
      const to = prompt("Last day, as YYYY-MM-DD.", b.dataset["to"] || from);
      if (to === null) return;
      const ok = (d: string) => !d || /^\d{4}-\d{2}-\d{2}$/.test(d.trim());
      if (!ok(from) || !ok(to)) { alert("Dates go in as YYYY-MM-DD."); return; }
      b.disabled = true; b.textContent = "Saving…";
      try {
        await setCovers(b.dataset["covers"] as string, from.trim() || null, to.trim() || null);
        await drawPayouts(host, role);
      } catch (e) {
        b.disabled = false; b.textContent = "set the period";
        alert(e instanceof Error ? e.message : String(e));
      }
    }));
}

/* ── my pay ───────────────────────────────────────────────────────── */

async function drawMine(host: HTMLElement): Promise<void> {
  const me = await meAsTech();
  if (!me) {
    host.innerHTML = `<div class="card pad"><p class="quiet">Your login is not linked to a
      technician record, so there are no hours to show. An admin can link them on the Team
      screen.</p></div>`;
    return;
  }

  const [rows, pays, mine] = await Promise.all([
    hoursIn(from, to, me.id), payoutsIn(from, to, me.id), statement(me.id, from, to),
  ]);
  const card = buildCard(mine, from, to, "Supercanic");
  lastCard = card;
  const hours = rows.reduce((t, r) => t + Number(r.hours), 0);
  const paid = pays.reduce((t, p) => t + Number(p.amount), 0);

  host.innerHTML = `
    <div id="mypay-clock"></div>
    ${cardHtml(card, false)}
    <div class="pstate-acts"><button class="btn primary" id="pc-save">Save as image</button></div>
    <div class="mtiles">
      <div class="mtile count"><div class="mtile-l">Your hours</div>
        <div class="mtile-v">${hours.toFixed(1)}</div>
        <div class="mtile-n">${rows.length} ${rows.length === 1 ? "shift" : "shifts"} this period</div></div>
      <div class="mtile money"><div class="mtile-l">Paid to you</div>
        <div class="mtile-v">${money(paid)}</div>
        <div class="mtile-n">${pays.length ? "recorded in this window" : "nothing yet"}</div></div>
    </div>
    <div class="card">
      <div class="card-head">Your shifts</div>
      ${rows.length ? rows.map((r) => `
        <div class="row">
          <div class="row-main">
            <div class="row-title"><span class="num">${esc(dt(r.worked_on))}</span></div>
            ${r.note ? `<div class="row-sub">${esc(r.note)}</div>` : ""}
          </div>
          <div class="row-side"><div class="amount">${Number(r.hours).toFixed(2)} h</div></div>
        </div>`).join("") : `<div class="empty">No hours in this window.</div>`}
    </div>
    <p class="quiet">If something here is wrong, tell the desk rather than working around it.
       This is what payroll reads.</p>
    <a class="btn ghost" href="#/timesheet">Open your timesheet</a>`;
  document.getElementById("pc-save")?.addEventListener("click", () => void saveImage(mine));
  // The clock, the same panel #/clock shows. A punch redraws My pay so the
  // shift lands under Your shifts as soon as it is logged.
  const clockHost = document.getElementById("mypay-clock");
  if (clockHost) await drawClockPanel(clockHost, () => void drawMine(host));
}

/* ── the two forms ────────────────────────────────────────────────── */

function sheet(title: string, body: string, saveLabel: string): void {
  document.getElementById("paysheet")?.remove();
  document.body.insertAdjacentHTML("beforeend", `
    <div class="sheet-back" id="paysheet">
      <div class="sheet" role="dialog" aria-modal="true">
        <div class="sheet-head"><div><h2>${esc(title)}</h2></div>
          <button class="sheet-x" id="ps-x" aria-label="Close">&times;</button></div>
        <div class="sheet-body">${body}</div>
        <div class="sheet-acts">
          <button class="btn primary" id="ps-save">${esc(saveLabel)}</button>
          <button class="btn ghost" id="ps-cancel">Cancel</button>
        </div>
        <div class="msg-slot" id="ps-msg"></div>
      </div>
    </div>`);
  const shut = () => document.getElementById("paysheet")?.remove();
  document.getElementById("ps-x")?.addEventListener("click", shut);
  document.getElementById("ps-cancel")?.addEventListener("click", shut);
  document.getElementById("paysheet")?.addEventListener("click", (e) => {
    if ((e.target as HTMLElement).id === "paysheet") shut();
  });
}

const val = (id: string) =>
  (document.getElementById(id) as HTMLInputElement | HTMLSelectElement | null)?.value.trim() ?? "";
const fail = (m: string) => {
  const el = document.getElementById("ps-msg");
  if (el) el.innerHTML = `<p class="msg bad">${esc(m)}</p>`;
};

function techSelect(id: string, techs: Tech[]): string {
  return `<label class="field"><span>Who</span><select id="${id}">
    ${techs.filter((t) => t.active).map((t) =>
      `<option value="${esc(t.id)}">${esc(t.name)}</option>`).join("")}
  </select></label>`;
}

async function hoursForm(role: Role, techs: Tech[]): Promise<void> {
  sheet("Log hours", `
    ${techSelect("h-tech", techs)}
    <label class="field"><span>Day worked</span>
      <input id="h-day" type="date" value="${esc(shopDayKey(new Date()))}"></label>
    <label class="field"><span>Hours</span>
      <input id="h-hours" type="number" step="0.25" min="0.25"></label>
    <label class="field"><span>Rate for this shift</span>
      <input id="h-rate" type="number" step="0.01"></label>
    <label class="field"><span>Note</span>
      <input id="h-note"></label>`, "Log it");

  document.getElementById("ps-save")?.addEventListener("click", async () => {
    const b = document.getElementById("ps-save") as HTMLButtonElement;
    b.disabled = true; b.textContent = "Saving…";
    try {
      await logHours(val("h-tech"), val("h-day"), Number(val("h-hours")),
        val("h-rate") ? Number(val("h-rate")) : null, val("h-note"));
      document.getElementById("paysheet")?.remove();
      void payPage(role);
    } catch (e) {
      b.disabled = false; b.textContent = "Log it";
      fail(e instanceof Error ? e.message : String(e));
    }
  });
}

/**
 * Correcting a shift. The reason is required and it is appended rather
 * than replacing anything: what the row used to say stays readable under
 * what it says now, because a week that changes with no explanation is how
 * a technician stops trusting the numbers.
 */
function fixHours(role: Role, row: Hours, who: string): void {
  sheet(`${who} — ${dt(row.worked_on)}`, `
    <label class="field"><span>Day worked</span>
      <input id="f-day" type="date" value="${esc(row.worked_on)}"></label>
    <label class="field"><span>Hours</span>
      <input id="f-hours" type="number" step="0.25" min="0.25" value="${esc(String(row.hours))}"></label>
    <label class="field"><span>Rate for this shift</span>
      <input id="f-rate" type="number" step="0.01"
        value="${row.rate != null ? esc(String(row.rate)) : ""}"></label>
    <label class="field"><span>Why it changed</span>
      <input id="f-why" maxlength="200"></label>
    <p class="quiet small">The old figures and this reason are kept on the row. Nothing is
       overwritten silently.</p>
    ${row.note ? `<div class="ed-sec">Already noted</div>
      <p class="quiet small" style="white-space:pre-line">${esc(row.note)}</p>` : ""}
    <div class="ed-sec">Or take it off</div>
    <p class="quiet small">For a shift logged against the wrong person, or logged twice.
       The reason is recorded on the job.</p>
    <button class="btn ghost" id="f-del">Remove this shift</button>`, "Save the correction");

  document.getElementById("ps-save")?.addEventListener("click", async () => {
    const b = document.getElementById("ps-save") as HTMLButtonElement;
    b.disabled = true; b.textContent = "Saving…";
    try {
      await correctHours(row.id, {
        hours: Number(val("f-hours")),
        rate: val("f-rate") ? Number(val("f-rate")) : null,
        worked_on: val("f-day"),
      }, val("f-why"));
      document.getElementById("paysheet")?.remove();
      void payPage(role);
    } catch (e) {
      b.disabled = false; b.textContent = "Save the correction";
      fail(e instanceof Error ? e.message : String(e));
    }
  });

  document.getElementById("f-del")?.addEventListener("click", async () => {
    const b = document.getElementById("f-del") as HTMLButtonElement;
    // Two taps. Removing a shift removes somebody's pay for that day.
    if (b.dataset["sure"] !== "1") {
      b.dataset["sure"] = "1"; b.textContent = "Really remove it?";
      return;
    }
    try {
      await removeHours(row.id, val("f-why"));
      document.getElementById("paysheet")?.remove();
      void payPage(role);
    } catch (e) {
      b.dataset["sure"] = ""; b.textContent = "Remove this shift";
      fail(e instanceof Error ? e.message : String(e));
    }
  });
}

async function payoutForm(role: Role, lines: Line[], done?: () => void): Promise<void> {
  const techs = lines.length ? lines.map((l) => l.tech) : await technicians();
  const owedBy = new Map(lines.map((l) => [l.tech.id, l.owed]));

  sheet("Record a payout", `
    ${techSelect("o-tech", techs)}
    <label class="field"><span>Amount</span>
      <input id="o-amount" type="number" step="0.01"></label>
    <label class="field"><span>How</span><select id="o-method">
      <option value="transfer">Zelle or transfer</option><option value="cash">Cash</option>
      <option value="check">Check</option><option value="other">Other</option></select></label>
    <label class="field"><span>Covers from</span><input id="o-from" type="date" value="${esc(from)}"></label>
    <label class="field"><span>Covers to</span><input id="o-to" type="date" value="${esc(to)}"></label>
    <label class="field"><span>Reference</span><input id="o-ref"></label>
    <p class="quiet small">This row is the only proof somebody was paid. Nothing else in the
       system counts as one.</p>`, "Record it");

  // Prefill with what they are owed, since that is the number nine times in ten.
  const pick = document.getElementById("o-tech") as HTMLSelectElement | null;
  const amount = document.getElementById("o-amount") as HTMLInputElement | null;
  const prefill = () => {
    const owed = owedBy.get(pick?.value ?? "");
    if (amount && owed && owed > 0) amount.value = owed.toFixed(2);
  };
  pick?.addEventListener("change", prefill);
  prefill();

  document.getElementById("ps-save")?.addEventListener("click", async () => {
    const b = document.getElementById("ps-save") as HTMLButtonElement;
    b.disabled = true; b.textContent = "Saving…";
    try {
      await recordPayout(val("o-tech"), Number(val("o-amount")), val("o-method"),
        val("o-from"), val("o-to"), val("o-ref"));
      document.getElementById("paysheet")?.remove();
      if (done) done(); else void payPage(role);
    } catch (e) {
      b.disabled = false; b.textContent = "Record it";
      fail(e instanceof Error ? e.message : String(e));
    }
  });
}
